import { DOC_SECTIONS, DOC_SLUGS } from "./sections";
import type { DocPage, DocSection } from "./types";

/**
 * Page registry.
 *
 * Pages are discovered from `./pages/*.ts` rather than a hand-maintained
 * barrel: the previous docs drifted precisely because every addition needed
 * edits across several parallel lists. Adding a page is now one file plus one
 * slug in `sections.ts`, and `missingPages()` / `orphanPages()` fail the build
 * if those two ever disagree.
 */
const modules = import.meta.glob<{ page: DocPage }>("./pages/*.ts", { eager: true });

const PAGES: Record<string, DocPage> = {};
for (const mod of Object.values(modules)) {
  if (mod?.page?.slug) {
    PAGES[mod.page.slug] = mod.page;
  }
}

export function getDocSections(): readonly DocSection[] {
  return DOC_SECTIONS;
}

export function getDocPage(slug: string): DocPage | undefined {
  return PAGES[slug];
}

/** Every page in reading order, skipping slugs with no authored file. */
export function allDocPages(): DocPage[] {
  return DOC_SLUGS.map((slug) => PAGES[slug]).filter((page): page is DocPage => Boolean(page));
}

export function docSectionFor(slug: string): DocSection | undefined {
  return DOC_SECTIONS.find((section) => section.pages.includes(slug));
}

/** `/docs` for the hub, `/docs/<slug>` for every other page. */
export function docHref(slug: string): string {
  return slug === "index" ? "/docs" : `/docs/${slug}`;
}

/** Slugs listed in a section with no page file behind them. Build-time guard. */
export function missingPages(): string[] {
  return DOC_SLUGS.filter((slug) => !PAGES[slug]);
}

/** Page files unreachable from any section. Build-time guard. */
export function orphanPages(): string[] {
  return Object.keys(PAGES).filter((slug) => !DOC_SLUGS.includes(slug));
}

export { DOC_SECTIONS, DOC_SLUGS };
